import * as React from 'react';


import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  FormControl,
  Grid,
  List,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import imgNoPrecedent from '../assets/Precedente_not_found.png';
import { apiService } from './../services/api/ApiService';
import FeedbackForm from './FeedbackForm';
import { useNavigate, useParams } from 'react-router-dom';
import AnalysisNotFound from './AnalysisNotFound/AnalysisNotFound';


interface Precedent {
  title: string;
  date: string;
  sentencia_name: string;
  sentencia_url: string;
  status: string;
  description: string;
  justification: string;
  relevant: string;
  similarity: number;
  resumen_principal: string;
  resultado_sentencia: string;
  fecha_sentencia: string;
  additional_info?: string;
}


interface PrecedentProps {
  precedent: Precedent;
  onCopy: (text: string) => void;
  onClick: (precedent: Precedent) => void;
  isActive: boolean;
  onLike: (title: string) => void;
  onDislike: (title: string) => void;
}


interface ViewPrecedentProps {
  setActiveItem: (item: string) => void;
}




function parseJustification(text: string) {
  const lines = text.split('\n').filter(line => line.trim() !== '');


  const result = {
    similitud_tematica: { descripcion: '', puntaje: null as number | null },
    impacto_derecho_administrativo: { descripcion: '', puntaje: null as number | null },
    posibles_precedentes: { descripcion: '', puntaje: null as number | null },
    afectacion_derechos_procesales: { descripcion: '', puntaje: null as number | null },
    impacto_practica_legal: { descripcion: '', puntaje: null as number | null },
    concepto_impugnacion: { descripcion: '', puntaje: null as number | null },
    conclusion: ''
  };


  type ResultKey = keyof typeof result;


  lines.forEach(line => {
    const cleanLine = line.trim();
    const lower = cleanLine.toLowerCase();


    const extractData = (key: ResultKey) => {
      if (key === 'conclusion') return;


      const match = cleanLine.match(/^.*?:\s*(\d+)\.\s*(.*)$/);
      if (match) {
        const score = parseInt(match[1], 10);
        if (score >= 1 && score <= 5) {
          result[key].puntaje = score;
          result[key].descripcion = match[2].trim();
        } else {
          console.warn(`Puntaje fuera de rango en: "${cleanLine}"`);
        }
      }
    };


    if (lower.startsWith('a) similitud temática')) {
      extractData('similitud_tematica');
    } else if (lower.startsWith('b) impacto en el derecho administrativo')) {
      extractData('impacto_derecho_administrativo');
    } else if (lower.startsWith('c) posibles precedentes')) {
      extractData('posibles_precedentes');
    } else if (lower.startsWith('d) afectación a derechos procesales')) {
      extractData('afectacion_derechos_procesales');
    } else if (lower.startsWith('e) impacto en la práctica legal')) {
      extractData('impacto_practica_legal');
    } else if (lower.startsWith('f) concepto de impugnación')) {
      extractData('concepto_impugnacion');
    } else if (lower.startsWith('en resumen')) {
      result.conclusion += cleanLine + ' ';
    }
  });


  result.conclusion = result.conclusion.trim();
  return result;
}


const formatReport = (report: string) => {
  const lines = report.split('\n');
  let formattedReport = '';
  let inList = false;


  lines.forEach((line) => {
    line = line.trim();


    if (/^\d+\.\s/.test(line)) {
      if (inList) {
        formattedReport += '</ul>';
        inList = false;
      }
      formattedReport += `<h3>${line}</h3>`;
    } else if (/^[-*•]\s/.test(line)) {
      if (!inList) {
        formattedReport += '<ul>';
        inList = true;
      }
      formattedReport += `<li>${line.slice(2)}</li>`;
    } else if (line === '') {
      if (inList) {
        formattedReport += '</ul>';
        inList = false;
      }
    } else {
      formattedReport += `<p>${line}</p>`;
    }
  });


  if (inList) {
    formattedReport += '</ul>';
  }


  return formattedReport;
};


const PrecedentItem: React.FC<PrecedentProps> = ({ precedent, onCopy, onClick, isActive, onLike, onDislike }) => {
  const [showFullText, setShowFullText] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);


  const handleClick = () => {
    setExpanded(!expanded);
  };


  const handleCopy = () => {
    navigator.clipboard.writeText(precedent.justification)
      .then(() => {
        setOpenSnackbar(true);
      })
      .catch(() => {
        setOpenSnackbar(true);
      });
  };
  console.log({precedent})
  const parsed = parseJustification(precedent.justification);
  const toggleText = () => {
    setShowFullText(!showFullText);
  };


  const maxTextLength = 200;
  const isTextLong = precedent.resultado_sentencia.length > maxTextLength;


  return (
    <Card
      sx={{
        mb: 4,
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '4px',
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        p: 4,
        fontFamily: 'Montserrat',
        cursor: 'pointer',
      }}
      onClick={() => onClick(precedent)}
    >
      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '32px',
                fontWeight: 600,
                color: '#1E1E1E',
                lineHeight: '120%',
              }}
            >
              {precedent.sentencia_name}
            </Typography>


          </Box>


          <Typography
            variant="body2"
            sx={{
              fontSize: '18px',
              color: '#4B5563',
              mt: 1,
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: '#002E74' }}>
              Fecha de Sentencia:
            </Box>{' '}
            {new Date(precedent.fecha_sentencia).toLocaleDateString('es-ES')}
          </Typography>
        </Box>


        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(precedent.resumen_principal);
            }}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#002E74',
              borderColor: '#002E74',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#E6ECF3',
                borderColor: '#002E74',
              },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 18, mr: 1 }} />
            Copiar Texto
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(precedent.sentencia_url, '_blank');
            }}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#002E74',
              borderColor: '#002E74',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#E6ECF3',
                borderColor: '#002E74',
              },
            }}
          >
            <DownloadIcon sx={{ fontSize: 18, mr: 1 }} />
            Descargar Precedente
          </Button>
        </Box>
      </Box>


      <Box display="flex" justifyContent="space-between" alignItems="start" mb={3}>
        <Typography sx={{ fontSize: '16px', color: '#111827', flex: 1 }}>
          {precedent.resumen_principal}
        </Typography>


        <Box
          sx={{
            backgroundColor: '#002E74',
            color: 'white',
            borderRadius: '8px',
            width: '100px',
            textAlign: 'center',
            padding: '16px 8px',
            ml: 3,
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
            {Math.round(precedent.similarity * 100)}%
          </Typography>
          <Typography sx={{ fontSize: '12px' }}>Similitud</Typography>
        </Box>
      </Box>


      <Box sx={{ borderBottom: '1px solid #E5E7EB', mb: 2 }} />


      {precedent.additional_info && (
        <Typography sx={{ fontSize: '14px', color: '#6B7280', mb: 2 }}>
          <strong>Información adicional:</strong> {precedent.additional_info}
        </Typography>
      )}


      <Typography sx={{ fontSize: '16px', color: '#111827', mb: 2 }}>
        {showFullText
          ? precedent.resultado_sentencia
          : `${precedent.resultado_sentencia.substring(0, maxTextLength)}...`}
      </Typography>


      <Box display="flex" gap={2} mb={3}>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onCopy(precedent.resultado_sentencia);
          }}
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: '#002E74',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#E6ECF3' },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 18, mr: 1 }} />
          Copiar Texto
        </Button>
        {isTextLong && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              toggleText();
            }}
            color="primary"
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            {showFullText ? 'Leer menos' : 'Leer más'}
          </Button>
        )}
      </Box>


      {precedent.justification && (
        <Box
          sx={{
            backgroundColor: precedent.relevant === 'Relevante' ? '#ECEFFF' : '#FEE2E2',
            border: `1px dashed ${precedent.relevant === 'Relevante' ? '#002E74' : '#D83B32'}`,
            borderRadius: '8px',
            padding: 3,
          }}
        >
          <Box display="flex" alignItems="start" mb={2}>
            <i
              className="fas fa-info-circle"
              style={{ color: '#3B82F6', fontSize: '1.5rem', marginRight: '10px' }}
            ></i>
            <Typography
              sx={{
                fontSize: '28px',
                fontWeight: 600,
                color: precedent.relevant === 'Relevante' ? '#1E1E1E' : '#D83B32',
                fontFamily: 'Montserrat',
              }}
            >
              Justificación
            </Typography>
            <Box display="flex" justifyContent="flex-end" width="100%" ml="auto">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                sx={{
                  textTransform: 'none',
                  color: '#002E74',
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 18, mr: 1 }} />
                Copiar texto
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
                sx={{
                  textTransform: 'none',
                  color: '#002E74',
                  ml: 2,
                }}
              >
                {expanded ? <ArrowUpward /> : <ArrowDownward />}
              </Button>
            </Box>
          </Box>


         <Collapse in={expanded}>
  <Box sx={{ fontSize: '16px', color: '#1F2937', lineHeight: '28px', p: 2 }}>
    {[
      { label: 'Similitud Temática', value: parsed.similitud_tematica },
      { label: 'Impacto en el Derecho Administrativo', value: parsed.impacto_derecho_administrativo },
      { label: 'Posibles Precedentes', value: parsed.posibles_precedentes },
      { label: 'Afectación a Derechos Procesales', value: parsed.afectacion_derechos_procesales },
      { label: 'Impacto en la Práctica Legal', value: parsed.impacto_practica_legal },
      { label: 'Concepto de Impugnación', value: parsed.concepto_impugnacion },
    ].map((item, index) => (
      <Box
        key={index}
        mb={2}
        pb={1}
        sx={{ borderBottom: '1px solid #E5E7EB' }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
          <Typography sx={{ fontWeight: 600 }}>
            Puntaje: {item.value.puntaje}/5
          </Typography>
        </Box>
        <Typography sx={{ pl: 1 }}>{item.value.descripcion}</Typography>
      </Box>
    ))}
    {parsed.conclusion && (
      <Box mt={3}>
        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Conclusión:</Typography>
        <Typography sx={{ pl: 1 }}>{parsed.conclusion}</Typography>
      </Box>
    )}
  </Box>
</Collapse>




        </Box>
      )}
    </Card>
  );
};
const ViewPrecedent: React.FC<{ setActiveItem: (item: string) => void }> = ({ setActiveItem }) => {
  const { precedentName } = useParams<{ precedentName: string }>();
  const decodedPdfFileName = precedentName ? decodeURIComponent(precedentName) : "";
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [selectedPrecedent, setSelectedPrecedent] = useState<any>(null);


  const [precedents, setPrecedents] = useState<Precedent[]>([]);
  const [filteredPrecedents, setFilteredPrecedents] = useState<Precedent[]>([]);
  const [activeItem, setActiveItemLocal] = useState<Precedent | null>(null);


  const [sortBy, setSortBy] = useState<'similarity' | 'alphabetical'>('similarity');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');


  // ⬇️ estados para pantalla vacía / carga
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  // Obtener el rol del usuario desde localStorage
  const userProfile = JSON.parse(localStorage.getItem('userProfile') || 'null');
  const isSupervisor = userProfile?.role === 'Supervisor';



  const sortPrecedents = (data: Precedent[], by: 'similarity' | 'alphabetical', order: 'asc' | 'desc') => {
    return [...data].sort((a, b) => {
      if (by === 'similarity') {
        return order === 'desc' ? b.similarity - a.similarity : a.similarity - b.similarity;
      }
      return order === 'desc'
        ? b.sentencia_name.localeCompare(a.sentencia_name)
        : a.sentencia_name.localeCompare(b.sentencia_name);
    });
  };


  // Cargar precedente desde URL params usando el nuevo método API
  useEffect(() => {
    const fetchPrecedent = async () => {
      if (!decodedPdfFileName) {
        console.log("Try3")
        setNotFound(true);
        setLoading(false);
        return;
      }


      setLoading(true);
      try {
        const precedent = await apiService.getPrecedenteByPdf(decodedPdfFileName);
        if (!precedent) {
          console.log("Try2")
          setNotFound(true);
          return;
        }


        setSelectedPrecedent(precedent);

      } catch (err) {
        console.error("Error al cargar precedente:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };


    fetchPrecedent();
  }, [decodedPdfFileName]);


  // 2) Carga de precedentes; si la API no devuelve nada -> pantalla "no encontrado"
  useEffect(() => {
    const fetchPrecedents = async () => {
      setLoading(true);
      if (!decodedPdfFileName || notFound) return;

      try {
        const precedentData = await apiService.getPrecedenteInfo(decodedPdfFileName);


        if (!Array.isArray(precedentData) || precedentData.length === 0) {
          console.log("Try1")
          //setNotFound(true);
          return;
        }


        const normalized: Precedent[] = precedentData.map((p: any) => ({
          ...p,
          fecha_sentencia: p.fecha_sentencia ?? '',
        }));


        const sorted = sortPrecedents(normalized, sortBy, sortOrder);
        setPrecedents(sorted);
        setFilteredPrecedents(sorted);
      } catch (err) {
        //setNotFound(true);
      } finally {
        setLoading(false);
      }
    };


    fetchPrecedents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedPdfFileName]);


  useEffect(() => {
    handleDateFilter();
  }, [precedents, dateFrom, dateTo]);


  const handleOrderChange = (order: 'asc' | 'desc') => {
    setSortOrder(order);
    setFilteredPrecedents(sortPrecedents(filteredPrecedents, sortBy, order));
  };


  const handleDateFilter = () => {
    if (!dateFrom && !dateTo) {
      setFilteredPrecedents(sortPrecedents(precedents, sortBy, sortOrder));
      return;
    }


    const filtered = precedents.filter((precedent) => {
      const sentenciaDate = new Date(precedent.fecha_sentencia);
      if (isNaN(sentenciaDate.getTime())) return true;


      const ymd = sentenciaDate.toISOString().split('T')[0];
      if (dateFrom && dateTo) return ymd >= dateFrom && ymd <= dateTo;
      if (dateFrom) return ymd >= dateFrom;
      if (dateTo) return ymd <= dateTo;
      return true;
    });


    setFilteredPrecedents(sortPrecedents(filtered, sortBy, sortOrder));
  };


  // Observe changes to precedents, dateFrom, and dateTo
  useEffect(() => {
    handleDateFilter();
  }, [precedents, dateFrom, dateTo]);

  const handleSortChange = (by: 'similarity' | 'alphabetical') => {
    setSortBy(by);
    setFilteredPrecedents(sortPrecedents(filteredPrecedents, by, sortOrder));
  };

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedText(text);
        setSnackbarOpen(true);
      })
      .catch((err) => console.error('Error al copiar texto: ', err));
  };


  const handleClickItem = (precedent: Precedent) => {
    setActiveItemLocal(precedent === activeItem ? null : precedent);
  };


  if (loading) {
    return (
      <Box sx={{ p: 6 }}>
        <Typography>Cargando precedentes…</Typography>
      </Box>
    );
  }


  if (notFound) {
    return (
      <AnalysisNotFound
        title="Lo sentimos, análisis no encontrado"
        subtitle="No pudimos localizar la información del análisis solicitado."
      />
    );
  }


  return (
    <Box sx={{ p: 6, backgroundColor: 'gray.100' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Análisis de Precedentes / {selectedPrecedent?.name}
        </Typography>
      </Box>


      <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" color="text-blue-600">
        {selectedPrecedent?.name}
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Cantidad de precedentes encontrados:{' '}
        <span style={{ fontWeight: 'bold', color: '#1E90FF' }}>{filteredPrecedents.length}</span>
      </Typography>


      <Card sx={{ mt: 3, mb: 2, backgroundColor: 'white', borderRadius: 2, p: 4, boxShadow: 1 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Información importante
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ backgroundColor: 'gray.50', p: 4, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="semibold">Caso:</Typography>
              <Typography variant="body2">{selectedPrecedent?.name ?? 'No disponible'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ backgroundColor: 'gray.50', p: 4, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="semibold">Número de caso/precedente:</Typography>
              <Typography variant="body2">{selectedPrecedent?.numberCase ?? 'No disponible'}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Card>


      <Card sx={{ mb: 2, p: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography
              variant="body1"
              dangerouslySetInnerHTML={{ __html: formatReport(selectedPrecedent?.resumen_pdf || '') }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            {selectedPrecedent && !isSupervisor && <FeedbackForm />}
          </Grid>
        </Grid>
      </Card>


      {/* Controles de orden/fecha */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          mb: 4,
          gap: 2,
        }}
      >
        <Typography variant="h6" component="h3" fontWeight="bold">
          Precedentes
        </Typography>


        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            fontSize: '0.875rem',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>Por similitud:</Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={sortOrder}
                onChange={(e) => handleOrderChange(e.target.value as 'asc' | 'desc')}
                sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { paddingY: '6px' } }}
              >
                <MenuItem value="desc">Mayor a menor</MenuItem>
                <MenuItem value="asc">Menor a mayor</MenuItem>
              </Select>
            </FormControl>
          </Box>


          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>De:</Typography>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '6px', fontSize: '0.875rem' }}
            />
          </Box>


          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>Hasta:</Typography>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '6px', fontSize: '0.875rem' }}
            />
          </Box>
        </Box>
      </Box>


      <List>
        {filteredPrecedents.length > 0 ? (
          filteredPrecedents.map((precedent, index) => (
            <PrecedentItem
              key={index}
              precedent={precedent}
              onCopy={handleCopy}
              onClick={handleClickItem}
              isActive={activeItem === precedent}
              onLike={() => {}}
              onDislike={() => {}}
            />
          ))
        ) : (
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <img
              src={imgNoPrecedent}
              alt="No Precedent Found"
              style={{ width: '452px', height: 'auto', marginBottom: '16px' }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '24px',
                fontWeight: 500,
                lineHeight: '32px',
                textAlign: 'center',
                color: '#AAAAAA',
              }}
            >
              No se encontró precedente
            </Typography>
          </Box>
        )}
      </List>


      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          ¡Texto copiado al portapapeles!
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default ViewPrecedent;

